import { captureException } from '@sentry/nextjs'
import {
  createJourneyValuesManager,
  getDefaultBrowserEnv,
  getJourneyQueryFromLocation,
  getLandingPageFromLocation,
  toProtoContext,
  type ProtoContext,
} from '@ccpgames/eve-web-hooks'
import { pushClientDownloadEventToDataLayer } from '@/lib/analytics/data-layer'

const DOWNLOAD_EVENT_ENDPOINT = '/api/events/download'
const LOCALHOST_DOWNLOAD_EVENT_ENDPOINT = 'https://dev.ccpeveweb.com/api/events/download'
const JOURNEY_ID_STORAGE_KEY = 'jid'

type ButtonPlatform = 'win' | 'mac'
type DownloadEventPlatform = 'windows' | 'mac'
type InstallerDownloadMessage = {
  context: ProtoContext
  platform: DownloadEventPlatform
  operatingSystem: string
  journeyId: string
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function isLocalHostname(hostname: string) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
}

function getSessionStorage() {
  if (!isBrowser()) {
    return null
  }

  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function getJourneyValues() {
  const manager = createJourneyValuesManager(getDefaultBrowserEnv())
  const existingValues = manager.getJourneyValues()

  if (existingValues) {
    return existingValues
  }

  manager.processFirstPageVisit(getLandingPageFromLocation(), getJourneyQueryFromLocation())
  return manager.getJourneyValues()
}

export function getOrCreateJourneyId() {
  const storage = getSessionStorage()
  const existingJourneyId = storage?.getItem(JOURNEY_ID_STORAGE_KEY)

  if (existingJourneyId) {
    return existingJourneyId
  }

  const journeyId = crypto.randomUUID()
  storage?.setItem(JOURNEY_ID_STORAGE_KEY, journeyId)
  return journeyId
}

export function getDownloadEventEndpoint(hostname?: string) {
  const resolvedHostname = hostname ?? (isBrowser() ? window.location.hostname : '')

  if (isLocalHostname(resolvedHostname)) {
    return LOCALHOST_DOWNLOAD_EVENT_ENDPOINT
  }

  return DOWNLOAD_EVENT_ENDPOINT
}

function getOperatingSystem() {
  if (!isBrowser()) {
    return 'unknown'
  }

  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: {
      platform?: string
    }
  }

  return navigatorWithUserAgentData.userAgentData?.platform ?? navigator.platform ?? navigator.userAgent
}

function mapPlatform(platform: ButtonPlatform): DownloadEventPlatform {
  return platform === 'win' ? 'windows' : 'mac'
}

export function createDownloadEventPayload(platform: ButtonPlatform): InstallerDownloadMessage | null {
  if (!isBrowser()) {
    return null
  }

  const journeyValues = getJourneyValues()

  return {
    context: toProtoContext(journeyValues),
    platform: mapPlatform(platform),
    operatingSystem: getOperatingSystem(),
    journeyId: getOrCreateJourneyId(),
  }
}

export async function sendDownloadEvent(platform: ButtonPlatform, downloadHref: string) {
  const payload = createDownloadEventPayload(platform)

  if (!payload) {
    return
  }

  try {
    const response = await fetch(getDownloadEventEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })

    if (!response.ok) {
      throw new Error(`Download event request failed with status ${response.status}`)
    }
  } catch (error) {
    captureException(error, {
      tags: {
        area: 'download-event',
      },
      extra: {
        platform: payload.platform,
      },
    })
  } finally {
    pushClientDownloadEventToDataLayer(payload.platform, downloadHref)
  }
}
